import type { CrmOption } from '@/types';
import {
  PLAY_CRM_NAMES,
  PLAY_CRMS,
  type CompanySignalConfig,
  type CompanySignalKind,
  type CrmAccountView,
} from '@/types/custom-playbooks';
import { normalizeDomain, normalizeDomainList } from '@/lib/normalize-domain';
import {
  CRM_ACCOUNTS_CONDITION,
  CRM_FIELD_OP_LABEL,
  parseFieldCondition,
} from '@/utils/crm-section-clauses.util';

export const COMPANY_SIGNALS: { value: CompanySignalKind; label: string }[] = [
  { value: 'job_posting', label: 'New job posting' },
  { value: 'news', label: 'Company news' },
  { value: 'funding', label: 'Funding round' },
  { value: 'headcount', label: 'Headcount growth' },
];
export const MAX_SIGNAL_ACCOUNTS = 10_000;
// SignalAccounts refuses these (cleanlist_crm_core FREE_EMAIL_DOMAINS).
const FREE_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'ymail.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'msn.com',
  'aol.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'proton.me',
  'protonmail.com',
  'gmx.com',
  'mail.com',
  'zoho.com',
  'yandex.com',
  'qq.com',
  '163.com',
  '126.com',
]);
/** The API's CrmAccountView condition limit. */
const MAX_CRM_CONDITION = 300;
/** The CRMs whose accounts a play can target, in the order they're offered:
 *  the play CRMs. The API's CrmAccountView takes these three; Pipedrive has
 *  no arm. */
const TARGET_ACCOUNT_CRMS = PLAY_CRMS;
/** A CRM account source the API reads: every account, or one account field. */
export function crmAccountViewValid(view: CrmAccountView): boolean {
  const { crmType, condition } = view;
  if (!TARGET_ACCOUNT_CRMS.includes(crmType)) return false;
  if (typeof condition !== 'string' || condition.length > MAX_CRM_CONDITION)
    return false;
  return (
    condition === CRM_ACCOUNTS_CONDITION ||
    (condition.startsWith('field:account.') &&
      parseFieldCondition(condition) !== null)
  );
}
/** What each CRM calls an account. */
const ACCOUNT_WORD: Record<CrmAccountView['crmType'], string> = {
  salesforce: 'accounts',
  hubspot: 'companies',
  attio: 'companies',
};
const CRM_NAME = PLAY_CRM_NAMES;

export interface TargetAccountCrm {
  type: CrmAccountView['crmType'];
  name: string;
}
/** The connected CRMs target accounts can read, from `/crm/options`. A CRM
 *  counts only while its connection is healthy (`onboarded` alone includes a
 *  revoked token, and every read 400s for those). Attio is listed when it is
 *  connected; no flag is read. */
export function targetAccountCrms(
  options: readonly CrmOption[] | undefined
): TargetAccountCrm[] {
  return TARGET_ACCOUNT_CRMS.flatMap((type) => {
    const option = options?.find(
      (o) => o.type === type && o.onboarded && o.is_connected
    );
    return option ? [{ type, name: option.name || CRM_NAME[type] }] : [];
  });
}

/** "Salesforce accounts: Type is Target", or "...: Every account". */
export function crmAccountViewLabel(view: CrmAccountView): string {
  const source = `${CRM_NAME[view.crmType] ?? view.crmType} ${ACCOUNT_WORD[view.crmType] ?? 'accounts'}`;
  if (view.condition === CRM_ACCOUNTS_CONDITION)
    return `${source}: Every account`;
  const parsed = parseFieldCondition(view.condition);
  if (!parsed) return `${source}: ${view.condition}`;
  const op = CRM_FIELD_OP_LABEL[parsed.op];
  return parsed.value === null
    ? `${source}: ${parsed.field} ${op}`
    : `${source}: ${parsed.field} ${op} ${parsed.value}`;
}

/** Pasted domains as they save: trimmed, with blank lines dropped. */
export function signalDomains(values: readonly unknown[]): string[] {
  return values.flatMap((value) =>
    typeof value === 'string' && value.trim() ? [value.trim()] : []
  );
}
export function companySignalIssue(
  config?: CompanySignalConfig
): string | null {
  if (
    !config ||
    !Array.isArray(config.signals) ||
    !config.signals.length ||
    config.signals.some(
      (value) => !COMPANY_SIGNALS.some((signal) => signal.value === value)
    ) ||
    new Set(config.signals).size !== config.signals.length
  )
    return 'Select at least one account signal';
  const accounts = config.accounts;
  if (!accounts || !Array.isArray(accounts.domains))
    return 'Choose named accounts';
  if (accounts.fromCrm) {
    if (accounts.fromList || signalDomains(accounts.domains).length)
      return 'Use one account source';
    if (!crmAccountViewValid(accounts.fromCrm))
      return 'Choose a CRM account condition';
  }
  if (accounts.domains.length > MAX_SIGNAL_ACCOUNTS)
    return 'Target at most 10,000 account domains';
  if (
    signalDomains(accounts.domains).some((value) => {
      const domain = normalizeDomain(value);
      return !domain || FREE_EMAIL_DOMAINS.has(domain);
    })
  )
    return 'Use company domains like acme.com, not personal email domains like gmail.com';
  if (
    accounts.fromList &&
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      accounts.fromList.listId
    )
  )
    return 'Choose a target account list';
  if (
    !accounts.fromList &&
    !accounts.fromCrm &&
    !normalizeDomainList(accounts.domains).length
  )
    return 'Choose named accounts';
  const phrases = [config.jobPosting?.titleKeywords, config.news?.keywords];
  if (
    phrases.some(
      (values) =>
        values &&
        (values.length > 20 ||
          values.some((value) => {
            const phrase = value.trim();
            return (
              !phrase ||
              phrase.length > 60 ||
              [...phrase].some((character) => {
                const code = character.charCodeAt(0);
                return code < 32 || code === 127;
              })
            );
          }))
    )
  )
    return 'Use up to 20 phrases, each 1 to 60 characters';
  if (
    config.jobPosting?.functions?.some(
      (value) =>
        !['Engineering', 'Sales', 'Operations', 'Others'].includes(value)
    )
  )
    return 'Choose a supported job function';
  const threshold = config.headcount?.growth6mMinPercent;
  if (
    threshold !== undefined &&
    threshold !== null &&
    (!Number.isFinite(threshold) || threshold < -100 || threshold > 1000)
  )
    return 'Headcount growth must be -100 to 1000 percent';
  if (config.freshness && config.freshness !== 'monthly')
    return 'Company updates use monthly freshness';
  return null;
}
